/**
 * @param {boolean} twoColumnView - A boolean that determines in how many columns contact info is shown.
 * @param {string} contactVariant - A string that informs which information will be shown per contact.
 * @param {Object[]} contactsToList - An array of serialized Contact records for display
 * @param {function} selectContact - A function that will be called when the marina wants to select a customer (Contact)
 * @param {function} importContact - A function that will be called when the marina wants to import a customer (Contact)
 * @param {string} brewerImportPath - A string representing the path to import a SafeHarbor Contact to a marina via the importContact function
 * @param {string} manageContactsPath - A string representing the path for a marina to view a customer (Contact)
 * @param {string} userImportPath - A string representing the path to import a Contact to a marina via the importContact function
 * @param {string} archivedContactsPath - A string representing the path to view archived contacts.
 *
 */
import ApplicationComponent from "legacy/ApplicationComponent"
import LegacyButton from "legacy/LegacyButton"
import pluralize from "pluralize"
import PropTypes from "prop-types"
import React from "react"

class ContactList extends ApplicationComponent {
  constructor(props) {
    super(props)

    this.networkForResultsBanner = this.networkForResultsBanner.bind(this)
    this.resultsBanner = this.resultsBanner.bind(this)
    this.contactRow = this.contactRow.bind(this)
  }

  networkForResultsBanner() {
    switch (this.props.contactVariant) {
      case "safe_harbor":
        return "Safe Harbor's"
      case "dockwa":
        return "Dockwa's"
      default:
        return null
    }
  }

  resultsBanner() {
    return (
      <tr>
        <td
          className="bg-teal-200 px-2 py-5"
          colSpan={this.props.twoColumnView ? "2" : "4"}
        >
          <strong>
            Your search matched the following{" "}
            {pluralize("boater", this.props.contactsToList.length, true)} from{" "}
            {this.networkForResultsBanner()} network:
          </strong>
        </td>
      </tr>
    )
  }

  contactRow(contact) {
    if (this.props.contactVariant === "direct") {
      return (
        <tr
          key={contact.id}
          className={
            this.props.twoColumnView ? "cursor-pointer hover:bg-gray-100" : ""
          }
          role={this.props.twoColumnView ? "button" : "row"}
          onClick={
            this.props.twoColumnView
              ? () => this.props.selectContact(contact)
              : undefined
          }
          onKeyPress={
            this.props.twoColumnView
              ? () => this.props.selectContact(contact)
              : undefined
          }
          tabIndex="0"
        >
          <td className="border-b border-gray-400 p-2">{contact.name}</td>
          <td
            style={{ maxWidth: "10rem", textOverflow: "ellipsis" }}
            className="overflow-hidden text-ellipsis whitespace-nowrap border-b border-gray-400 p-1"
          >
            {contact.email}
          </td>
          {!this.props.twoColumnView && (
            <td className="border-b border-gray-400 p-2">
              {contact.displayPhone}
            </td>
          )}
          {!this.props.twoColumnView && (
            <td className="space-x-1 border-b border-gray-400 p-2 text-right">
              <a
                target="_blank"
                rel="noreferrer"
                href={`${this.props.manageContactsPath}/${contact.encodedId}`}
                className="btn btn-tertiary btn-sm"
              >
                View
              </a>
              <LegacyButton
                type="primary"
                className="btn-sm"
                onClick={() => this.props.selectContact(contact)}
              >
                Select
              </LegacyButton>
            </td>
          )}
        </tr>
      )
    } else if (this.props.contactVariant === "archived") {
      return (
        <tr key={contact.id} tabIndex="0">
          <td className="py-4" colSpan={4}>
            <div className="flex flex-col space-y-2 text-center">
              <strong>
                Your search matched contacts that are currently archived
              </strong>
              <a
                className="font-bold text-blue-800"
                href={this.props.archivedContactsPath}
              >
                View archived contacts
              </a>
            </div>
          </td>
        </tr>
      )
    } else if (this.props.contactVariant === "safe_harbor") {
      return (
        <tr
          key={contact.encodedId}
          className={
            this.props.twoColumnView ? "cursor-pointer hover:bg-gray-100" : ""
          }
          role={this.props.twoColumnView ? "button" : "row"}
          onClick={
            this.props.twoColumnView
              ? () =>
                  this.props.importContact(
                    this.props.brewerImportPath,
                    contact.encodedId
                  )
              : undefined
          }
          onKeyPress={
            this.props.twoColumnView
              ? () =>
                  this.props.importContact(
                    this.props.brewerImportPath,
                    contact.encodedId
                  )
              : undefined
          }
          tabIndex="0"
        >
          <td className="border-b border-gray-400 p-2">{contact.name}</td>
          <td
            style={{ maxWidth: "10rem", textOverflow: "ellipsis" }}
            className="overflow-hidden text-ellipsis whitespace-nowrap border-b border-gray-400 p-1"
          >
            {contact.email}
          </td>
          {!this.props.twoColumnView && (
            <td className="border-b border-gray-400 p-2">
              {contact.displayPhone}
            </td>
          )}
          {!this.props.twoColumnView && (
            <td className="space-x-1 border-b border-gray-400 p-2 text-right">
              <button
                className="btn btn-danger btn-sm"
                type="button"
                onClick={() =>
                  this.props.importContact(
                    this.props.brewerImportPath,
                    contact.encodedId
                  )
                }
                style={{ minWidth: 160 }}
              >
                Import Contact
              </button>
            </td>
          )}
        </tr>
      )
    } else if (this.props.contactVariant === "dockwa") {
      return (
        <tr
          key={contact.id}
          className={
            this.props.twoColumnView ? "cursor-pointer hover:bg-gray-100" : ""
          }
          role={this.props.twoColumnView ? "button" : "row"}
          onClick={
            this.props.twoColumnView
              ? () =>
                  this.props.importContact(
                    this.props.userImportPath,
                    contact.id
                  )
              : undefined
          }
          onKeyPress={
            this.props.twoColumnView
              ? () =>
                  this.props.importContact(
                    this.props.userImportPath,
                    contact.id
                  )
              : undefined
          }
          tabIndex="0"
        >
          <td className="border-b border-gray-400 p-2">{contact.name}</td>
          <td
            style={{ maxWidth: "10rem", textOverflow: "ellipsis" }}
            className="overflow-hidden text-ellipsis whitespace-nowrap border-b border-gray-400 p-1"
          >
            {contact.email}
          </td>
          {!this.props.twoColumnView && (
            <td className="border-b border-gray-400 p-2">
              {contact.displayPhone}
            </td>
          )}
          {!this.props.twoColumnView && (
            <td className="space-x-1 border-b border-gray-400 p-2 text-right">
              <button
                className="btn btn-danger btn-sm"
                type="button"
                onClick={() =>
                  this.props.importContact(
                    this.props.userImportPath,
                    contact.id
                  )
                }
                style={{ minWidth: 160 }}
              >
                Import Contact
              </button>
            </td>
          )}
        </tr>
      )
    }
    return null
  }

  render() {
    return (
      <tbody>
        {this.networkForResultsBanner() && this.resultsBanner()}
        {this.props.contactsToList.map((contact) => this.contactRow(contact))}
      </tbody>
    )
  }
}

ContactList.propTypes = {
  twoColumnView: PropTypes.bool,
  contactVariant: PropTypes.oneOf([
    "direct",
    "archived",
    "safe_harbor",
    "dockwa",
  ]),
  contactsToList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      encodedId: PropTypes.string,
      name: PropTypes.string,
      email: PropTypes.string,
      displayPhone: PropTypes.string,
    })
  ).isRequired,
  selectContact: PropTypes.func,
  importContact: PropTypes.func,
  brewerImportPath: PropTypes.string,
  manageContactsPath: PropTypes.string,
  userImportPath: PropTypes.string,
  archivedContactsPath: PropTypes.string,
}

export default ContactList
